import React, {useEffect, useState} from 'react'
import i18tn from 'i18next'

import PropTypes from 'prop-types'
import Button from '#components/a/Button'
import ModalByProps from '#components/m/ModalByProps'

import DropdownSelect from '#components/m/DropdownSelect'
import Headline from '#components/a/Headline'
import FileInput from '#components/a/form/FileInput'
import Text from '#components/a/Text'

const maxFileSizeBytes = 20_000_000
const supportedMimeTypes = ['application/pdf']

function UploadFeedbackBaseModal({
  apiCallback,
  onSuccess,
  isLoadingOptions,
  dropdownOptions,
  dropdownPlaceholder,
  confirmDisabledReason,
  uploadDescription,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState()
  const [selectedOption, setSelectedOption] = useState()

  const closeModal = () => {
    setSelectedOption()
    setSelectedFile()
    setError()
    setIsModalOpen(false)
  }

  const handleConfirm = async () => {
    setIsUploading(true)
    try {
      const response = await apiCallback(selectedFile, selectedOption)
      onSuccess(response)
      closeModal()
    } catch (e) {
      if (e.message === 'feedback_upload_not_a_pdf_file') {
        setError(i18tn.t('upload_feedback_modal_file_not_supported'))
        return
      }
      if (e.message === 'feedback_upload_file_too_large') {
        setError(i18tn.t('upload_feedback_modal_file_to_large'))
        return
      }

      throw e
    } finally {
      setIsUploading(false)
    }
  }

  const validateFile = file => {
    if (!file) return null
    if (file.size > maxFileSizeBytes) {
      return i18tn.t('upload_feedback_modal_file_to_large')
    }

    if (!supportedMimeTypes.includes(file.type)) {
      return i18tn.t('upload_feedback_modal_file_not_supported')
    }

    return null
  }

  useEffect(() => {
    setError(validateFile(selectedFile))
  }, [selectedFile])

  const isLoading = isLoadingOptions || isUploading
  const hasError = !!error
  const isConfirmButtonDisbaled = !selectedOption || !selectedFile || hasError || isLoading
  const isDropdownDisabled = isLoadingOptions

  return (
    <>
      <Button
        testid="upload-feedback-button"
        text={i18tn.t('feedback_show_upload_modal_button')}
        variant="primary"
        event={() => setIsModalOpen(true)}
      />

      <ModalByProps
        modifier="upload-feedback"
        testId="uploadFeedbackModal"
        modalIsOpen={isModalOpen}
        closeModal={closeModal}
        confirmText={i18tn.t('upload_feedback_modal_confirm_button')}
        callbackIsExecuting={isLoading}
        handleConfirm={handleConfirm}
        confirmButtonDisabled={isConfirmButtonDisbaled}
        confirmButtonDisabledReason={confirmDisabledReason}
      >
        <div className="uploadFeedbackModal">
          <Headline modifier="article" importance={2} text={i18tn.t('upload_feedback_modal_headline')} />
          <div className="uploadDescription">{uploadDescription}</div>

          <DropdownSelect
            options={dropdownOptions}
            isDisabled={isDropdownDisabled}
            placeholder={dropdownPlaceholder}
            selectedOption={selectedOption}
            handleSelect={option => setSelectedOption(option)}
          />

          <FileInput
            onFileChanged={setSelectedFile}
            selectedFile={selectedFile}
            placeholder={i18tn.t('upload_feedback_modal_file_input_placeholder')}
            accept={supportedMimeTypes.join(', ')}
          />

          {hasError && (
            <div className="errorMessage">
              <Text modifier="warning" text={error} />
            </div>
          )}
        </div>
      </ModalByProps>
    </>
  )
}

UploadFeedbackBaseModal.propTypes = {
  apiCallback: PropTypes.func,
  onSuccess: PropTypes.func,
  isLoadingOptions: PropTypes.bool,
  dropdownOptions: PropTypes.array,
  dropdownPlaceholder: PropTypes.string,
  confirmDisabledReason: PropTypes.string,
  uploadDescription: PropTypes.string,
}

export default UploadFeedbackBaseModal
